import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { BillingContext } from "@/context/BillingContext";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Home,
  CalendarDays,
  ClipboardList,
  Users,
  Boxes,
  Camera,
  Wallet,
  FileText,
  FileSignature,
  Calculator,
  UserPlus,
  CreditCard,
  Settings,
  LogOut,
  Lock,
  Loader2,
} from "lucide-react";

const nav = [
  { to: "/owner", end: true, label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/owner/properties", label: "Properties", icon: Home, id: "properties" },
  { to: "/owner/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/owner/jobs", label: "Cleaning Jobs", icon: ClipboardList, id: "jobs" },
  { to: "/owner/cleaners", label: "Team", icon: Users, id: "cleaners" },
  { to: "/owner/inventory", label: "Inventory", icon: Boxes, id: "inventory" },
  { to: "/owner/photos", label: "Photos & Damages", icon: Camera, id: "photos" },
  { to: "/owner/payouts", label: "Payouts", icon: Wallet, id: "payouts" },
  { to: "/owner/invoices", label: "Invoices", icon: FileText, id: "invoices" },
  { to: "/owner/estimate", label: "Estimate", icon: Calculator, id: "estimate", premium: true },
  { to: "/owner/leads", label: "Leads", icon: UserPlus, id: "leads", premium: true },
  { to: "/owner/contracts", label: "Contracts", icon: FileSignature, id: "contracts", premium: true },
  { to: "/owner/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/owner/settings", label: "Settings", icon: Settings, id: "settings" },
];

// Statuses that grant dashboard access; others are sent to billing to pay/start trial.
const ACTIVE_STATUSES = ["trialing", "active"];

export default function OwnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null);
  const [upgradeFor, setUpgradeFor] = useState(null);

  const loadBilling = useCallback(() => {
    return api
      .get("/billing")
      .then((res) => {
        setInfo(res.data);
        if (!ACTIVE_STATUSES.includes(res.data.subscription_status)) {
          navigate("/owner/billing", { replace: true });
        }
        return res.data;
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    loadBilling().finally(() => setChecking(false));
  }, [loadBilling]);

  const isPremium = info?.tier === "premium";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const renderNavItem = (n, isMobile) => {
    const locked = n.premium && !isPremium;
    const Icon = n.icon;
    if (locked) {
      const base = isMobile
        ? "flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] text-muted-foreground/60"
        : "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground/60 hover:bg-muted/60 transition-colors";
      return (
        <button
          key={n.to}
          onClick={() => setUpgradeFor(n.label)}
          data-testid={`nav-${n.id}-locked`}
          className={base}
        >
          <span className="relative">
            <Icon className={isMobile ? "h-5 w-5" : "h-[18px] w-[18px]"} strokeWidth={1.7} />
          </span>
          {!isMobile && (
            <>
              <span>{n.label}</span>
              <Lock className="h-3.5 w-3.5 ml-auto" />
            </>
          )}
        </button>
      );
    }
    return (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        data-testid={`nav-${n.id}`}
        className={({ isActive }) =>
          isMobile
            ? `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            : `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-secondary text-secondary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
        }
      >
        <Icon className={isMobile ? "h-5 w-5" : "h-[18px] w-[18px]"} strokeWidth={1.7} />
        {!isMobile && n.label}
      </NavLink>
    );
  };

  return (
    <BillingContext.Provider value={{ info, tier: info?.tier || "starter", isPremium, refresh: loadBilling }}>
      <div className="min-h-screen flex bg-background">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card fixed h-screen">
          <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
            <img src="/logo.png" alt="SmartHost Solutions" className="h-9 w-9 object-contain" />
            <span className="font-display text-lg font-bold text-foreground leading-tight">
              SmartHost
            </span>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {nav.map((n) => renderNavItem(n, false))}
          </nav>
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                {user?.name?.[0]?.toUpperCase() || "O"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate capitalize">
                  {info?.tier === "premium" ? "Premium plan" : "Starter plan"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 inset-x-0 z-20 h-14 bg-card border-b border-border flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="SmartHost" className="h-7 w-7 object-contain" />
            <span className="font-display font-bold text-foreground">SmartHost</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-card border-t border-border flex justify-around py-1.5 overflow-x-auto scrollbar-thin">
          {nav.map((n) => renderNavItem(n, true))}
        </div>

        <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0">
          <Outlet />
        </main>

        <UpgradeDialog
          open={!!upgradeFor}
          onOpenChange={(o) => !o && setUpgradeFor(null)}
          feature={upgradeFor}
        />
      </div>
    </BillingContext.Provider>
  );
}
